import axios from 'axios';
import { SalesEvaluation, EvaluationStatus } from '../types/sales';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export interface User {
  id: number;
  email: string;
  full_name: string;
  roles: string[];  // Multi-role support: array of role strings
  is_active: boolean;
  customer_id?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface UserCreate {
  email: string;
  full_name: string;
  roles: string[];  // Multi-role support: array of role strings
  password: string;
  customer_id?: string;
}

export interface UserUpdate {
  email?: string;
  full_name?: string;
  roles?: string[];  // Multi-role support: array of role strings
  is_active?: boolean;
  customer_id?: string;
}

// Auth
export const login = async (credentials: LoginCredentials) => {
  const response = await api.post('/auth/login-json', credentials);
  return response.data;
};

// Users
export const getUsers = async () => {
  const response = await api.get<User[]>('/admin/users');
  return response.data;
};

export const createUser = async (user: UserCreate) => {
  const response = await api.post<User>('/admin/users', user);
  return response.data;
};

export const updateUser = async (userId: number, user: UserUpdate) => {
  const response = await api.put<User>(`/admin/users/${userId}`, user);
  return response.data;
};

export const deleteUser = async (userId: number) => {
  await api.delete(`/admin/users/${userId}`);
};

export const resetUserPassword = async (userId: number, newPassword: string) => {
  await api.post(`/admin/users/${userId}/reset-password`, { new_password: newPassword });
};

// Companies
export interface Company {
  id: string;
  name: string;
  code: string;
  ruc?: string;
  address?: string;
  phone?: string;
  email?: string;
  logo_url?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export interface CompanyCreate {
  name: string;
  code: string;
  ruc?: string;
  address?: string;
  phone?: string;
  email?: string;
}

export interface CompanyUpdate {
  name?: string;
  code?: string;
  ruc?: string;
  address?: string;
  phone?: string;
  email?: string;
  is_active?: boolean;
}

export const getCompanies = async (isActive?: boolean) => {
  const params = isActive !== undefined ? `?is_active=${isActive}` : '';
  const response = await api.get<Company[]>(`/mps/companies${params}`);
  return response.data;
};

export const getCompany = async (companyId: string) => {
  const response = await api.get<Company>(`/mps/companies/${companyId}`);
  return response.data;
};

export const createCompany = async (company: CompanyCreate) => {
  const response = await api.post<Company>('/mps/companies/', company);
  return response.data;
};

export const updateCompany = async (companyId: string, company: CompanyUpdate) => {
  const response = await api.put<Company>(`/mps/companies/${companyId}`, company);
  return response.data;
};

export const deleteCompany = async (companyId: string) => {
  await api.delete(`/mps/companies/${companyId}`);
};

export const uploadCompanyLogo = async (companyId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(`/mps/companies/${companyId}/upload-logo`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Reports
export interface DailyDispatchSummary {
  dispatch_date: string;
  total_dispatches: number;
  total_cost: number;
  oem_dispatches: number;
  oem_cost: number;
  generic_dispatches: number;
  generic_cost: number;
  parts_dispatches: number;
  parts_cost: number;
  consumables_dispatches: number;
  consumables_cost: number;
  unique_clients: number;
}

export interface DispatchDetail {
  assigned_date: string;
  supply_id: string;
  supply_sku: string;
  supply_name: string;
  supply_type: string;  // 'consumable', 'part', 'maintenance', 'toner', 'drum', 'other'
  is_original: boolean;
  manufacturer: string | null;
  quantity: number;
  cost_per_unit: number;
  total_cost: number;
  equipment_serial: string;
  equipment_model: string | null;
  contract_number?: string | null;
  client_id: string;
  client_name: string;
  location: string | null;
  assigned_by: string | null;
  notes: string | null;
}

export interface DailyTonerDispatchReport {
  report_start_date: string;
  report_end_date: string;
  summary: DailyDispatchSummary[];
  dispatches: DispatchDetail[];
  grand_total_cost: number;
  grand_total_dispatches: number;
  grand_total_oem_cost: number;
  grand_total_generic_cost: number;
  grand_total_parts_dispatches: number;
  grand_total_parts_cost: number;
  grand_total_consumables_dispatches: number;
  grand_total_consumables_cost: number;
}

export const getDailyTonerDispatchReport = async (startDate?: string, endDate?: string) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  const queryString = params.toString();
  const url = `/reports/daily-toner-dispatches${queryString ? `?${queryString}` : ''}`;

  const response = await api.get<DailyTonerDispatchReport>(url);
  return response.data;
};

// Clients
export interface AssignedUserInfo {
  id: number;
  full_name: string;
  email: string;
}

export interface Client {
  customer_id: string;
  razon_social: string;
  ruc: string;
  address?: string;
  phone?: string;
  ekm_client_id?: string;
  is_active: boolean;
  payment_terms: string;
  created_at: string;
  updated_at?: string;
  assigned_to_user_id?: number;
  assigned_to_user?: AssignedUserInfo;
}

export interface ClientsResponse {
  items: Client[];
  total: number;
  skip: number;
  limit: number;
}

export const getClients = async (skip: number = 0, limit: number = 50, search?: string) => {
  const params = new URLSearchParams();
  params.append('skip', skip.toString());
  params.append('limit', limit.toString());
  if (search) {
    params.append('search', search);
  }
  const response = await api.get<ClientsResponse>(`/clients/?${params.toString()}`);
  return response.data;
};

export const getClient = async (clientId: string) => {
  const response = await api.get<Client>(`/clients/${clientId}`);
  return response.data;
};

export const assignClientToUser = async (clientId: string, userId: number) => {
  const response = await api.put<Client>(`/clients/${clientId}/assign-user/${userId}`);
  return response.data;
};

export const unassignClientFromUser = async (clientId: string) => {
  const response = await api.put<Client>(`/clients/${clientId}/unassign-user`);
  return response.data;
};

export const getVentasUsers = async () => {
  const allUsers = await getUsers();
  // Filter users who have VENTAS role (supports multi-role)
  return allUsers.filter(user => user.roles.includes('VENTAS') && user.is_active);
};

// MFP Models
export interface MFPModel {
  id: string;
  manufacturer: string;
  model_name: string;
  model_code: string;
  equipment_cost: number;
  is_color: boolean;
  is_active: boolean;
  is_sellable: boolean;
  image_url?: string;
  created_at: string;
  updated_at?: string;
}

export const mfpModelsAPI = {
  getAll: async (params?: { is_active?: boolean; is_sellable?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (params?.is_active !== undefined) {
      queryParams.append('is_active', String(params.is_active));
    }
    if (params?.is_sellable !== undefined) {
      queryParams.append('is_sellable', String(params.is_sellable));
    }

    const queryString = queryParams.toString();
    const url = `/mps/models/${queryString ? `?${queryString}` : ''}`;

    const response = await api.get<MFPModel[]>(url);
    return response.data;
  },

  toggleSellable: async (modelId: string, isSellable: boolean) => {
    const response = await api.patch<MFPModel>(
      `/mps/models/${modelId}/sellable?is_sellable=${isSellable}`
    );
    return response.data;
  },

  uploadImage: async (modelId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`/mps/models/${modelId}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteImage: async (modelId: string) => {
    const response = await api.delete(`/mps/models/${modelId}/image`);
    return response.data;
  },

  updateCostBreakdown: async (modelId: string, costData: {
    base_equipment_cost: number;
    distributor_margin_pct?: number;
    import_tax_pct?: number;
  }) => {
    const response = await api.put(`/mps/models/${modelId}`, costData);
    return response.data;
  },

  autocomplete: async (search?: string, limit: number = 50) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('limit', limit.toString());
    const response = await api.get(`/mps/models/autocomplete?${params.toString()}`);
    return response.data;
  },
};

// Client Merge
export interface ClientSummary {
  customer_id: string;
  razon_social: string;
  ruc: string;
  source_company?: string;
  is_active: boolean;
  phone?: string;
  address?: string;
  ekm_client_id?: string;
  assigned_to_user_id?: number;
  contracts_count: number;
  equipment_count: number;
  invoices_count: number;
  ar_count: number;
}

export interface DuplicatePair {
  client1: ClientSummary;
  client2: ClientSummary;
  similarity: number;
  match_type: 'exact' | 'fuzzy' | 'manual';
  recommended_primary: string;
  recommended_action: 'merge' | 'review' | 'ignore';
}

export interface DataMigrationSummary {
  accounts_receivable: number;
  ar_history_snapshots: number;
  client_contacts: number;
  client_payment_analytics: number;
  client_portal_invitations: number;
  client_profitability: number;
  contracts: number;
  contract_renewals: number;
  invoices: number;
  mfp_equipment: number;
  payment_arrangements: number;
  payment_proofs: number;
  payments: number;
  service_contacts: number;
}

export interface ConflictResolution {
  type: string;
  primary_value: any;
  secondary_value: any;
  resolution: string;
  kept_value: any;
  requires_user_decision?: boolean;
}

export interface MergeAnalysis {
  primary_client: ClientSummary;
  secondary_client: ClientSummary;
  data_to_migrate: DataMigrationSummary;
  conflicts: ConflictResolution[];
  warnings: string[];
  estimated_duration_seconds: number;
  is_safe: boolean;
}

export interface MergeResult {
  success: boolean;
  primary_client_id: string;
  secondary_client_id: string;
  merge_log_id?: string;
  data_migrated: DataMigrationSummary;
  conflicts_resolved: ConflictResolution[];
  message: string;
}

export const clientMergeAPI = {
  getDuplicates: async (params?: {
    include_exact?: boolean;
    include_fuzzy?: boolean;
    min_similarity?: number;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.include_exact !== undefined) queryParams.append('include_exact', String(params.include_exact));
    if (params?.include_fuzzy !== undefined) queryParams.append('include_fuzzy', String(params.include_fuzzy));
    if (params?.min_similarity) queryParams.append('min_similarity', String(params.min_similarity));
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const response = await api.get(`/client-merge/duplicates?${queryParams.toString()}`);
    return response.data;
  },

  analyzeMerge: async (primaryClientId: string, secondaryClientId: string) => {
    const response = await api.get<MergeAnalysis>(
      `/client-merge/analyze?primary_client_id=${primaryClientId}&secondary_client_id=${secondaryClientId}`
    );
    return response.data;
  },

  executeMerge: async (data: {
    primary_client_id: string;
    secondary_client_id: string;
    merge_strategy?: 'primary_only' | 'merge_attributes' | 'keep_complete';
    chosen_ruc?: string;
    notes?: string;
    dry_run?: boolean;
  }) => {
    const response = await api.post<MergeResult>('/client-merge/merge', data);
    return response.data;
  },

  getAuditLog: async (params?: { skip?: number; limit?: number; include_undone?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (params?.skip) queryParams.append('skip', String(params.skip));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.include_undone !== undefined) queryParams.append('include_undone', String(params.include_undone));

    const response = await api.get(`/client-merge/audit-log?${queryParams.toString()}`);
    return response.data;
  },
};

// Supply Import
export interface PendingSupplyItem {
  sku: string;
  description: string;
  brand: string;
  line_name: string;
  cost: number;
  stock: number;
  location: string;
  suggested_category: string;
  suggested_type: string;
  confidence: string;
  reason: string;
}

export interface PendingSuppliesResponse {
  total_pending: number;
  high_confidence: number;
  medium_confidence: number;
  low_confidence: number;
  items: PendingSupplyItem[];
}

export interface SupplyImportCreate {
  sku: string;
  name: string;
  type: string;
  category?: string;
  manufacturer?: string;
  unit_cost: number;
  stock_quantity: number;
  reorder_level?: number;
  yield_pages?: number;
  is_original: boolean;
  usage_type: string; // 'both', 'rental', 'sales'
  compatible_model_ids: string[];
}

export interface AccessoryImportCreate {
  sku: string;
  name: string;
  type?: string;
  manufacturer?: string;
  description?: string;
  unit_cost: number;
  stock_quantity: number;
  reorder_level?: number;
  compatible_model_ids: string[];
}

export interface ImportStats {
  pending_count: number;
  matched_count: number;
  total_supplies_in_db: number;
  total_accessories_in_db: number;
}

export const supplyImportAPI = {
  getPending: async (params?: {
    skip?: number;
    limit?: number;
    confidence?: string;
    category?: string;
    manufacturer?: string;
    has_duplicates?: boolean;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.skip !== undefined) queryParams.append('skip', String(params.skip));
    if (params?.limit !== undefined) queryParams.append('limit', String(params.limit));
    if (params?.manufacturer) queryParams.append('manufacturer', params.manufacturer);
    if (params?.has_duplicates !== undefined) queryParams.append('has_duplicates', String(params.has_duplicates));

    // Use database-based endpoint instead of CSV (works in Cloud Run)
    const response = await api.get<{ items: any[]; total: number }>(
      `/mps/supply-import/pending-db?${queryParams.toString()}`
    );

    // Adapt response to match expected format
    return {
      items: response.data.items,
      total_pending: response.data.total,
      high_confidence: 0,
      medium_confidence: 0,
      low_confidence: 0,
    };
  },

  createSupply: async (data: SupplyImportCreate) => {
    const response = await api.post('/mps/supply-import/create-supply', data);
    return response.data;
  },

  createAccessory: async (data: AccessoryImportCreate) => {
    const response = await api.post('/mps/supply-import/create-accessory', data);
    return response.data;
  },

  skipItem: async (sku: string) => {
    const response = await api.post(`/mps/supply-import/skip-item/${sku}`);
    return response.data;
  },

  searchModels: async (query: string, manufacturer?: string, limit: number = 20) => {
    const queryParams = new URLSearchParams();
    queryParams.append('query', query);
    if (manufacturer) queryParams.append('manufacturer', manufacturer);
    queryParams.append('limit', String(limit));

    const response = await api.get(`/mps/supply-import/models/search?${queryParams.toString()}`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get<ImportStats>('/mps/supply-import/stats');
    return response.data;
  },
};

// Supply Merge
export interface SupplySummary {
  id: string;
  sku: string;
  name: string;
  manufacturer?: string;
  type: string;
  category?: string;
  unit_cost: number;
  stock_quantity: number;
  is_original: boolean;
  is_active: boolean;
  created_at: string;
  model_supplies_count: number;
  assignments_count: number;
  inventory_movements_count: number;
}

export interface DuplicateSupplyPair {
  supply1: SupplySummary;
  supply2: SupplySummary;
  similarity: number;
  match_type: string;
  recommended_primary: string;
  recommended_action: string;
}

export interface SupplyMergeAnalysis {
  primary_supply: SupplySummary;
  secondary_supply: SupplySummary;
  data_to_migrate: {
    model_supplies: number;
    assignments: number;
    inventory_movements: number;
  };
  conflicts: Array<{
    type: string;
    primary_value: any;
    secondary_value: any;
    resolution: string;
    kept_value: any;
  }>;
  warnings: string[];
  is_safe: boolean;
}

export interface SupplyMergeRequest {
  primary_supply_id: string;
  secondary_supply_id: string;
  merge_strategy?: string;
  notes?: string;
  dry_run?: boolean;
}

export interface SupplyMergeResult {
  success: boolean;
  primary_supply_id: string;
  secondary_supply_id: string;
  data_migrated: {
    model_supplies: number;
    assignments: number;
    inventory_movements: number;
  };
  conflicts_resolved: Array<{
    type: string;
    action: string;
    result: any;
  }>;
  message: string;
}

export const supplyMergeAPI = {
  getDuplicates: async (params?: {
    min_similarity?: number;
    limit?: number;
    exclude_hp?: boolean;
    manufacturer_filter?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.min_similarity) queryParams.append('min_similarity', String(params.min_similarity));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.exclude_hp) queryParams.append('exclude_hp', String(params.exclude_hp));
    if (params?.manufacturer_filter) queryParams.append('manufacturer_filter', params.manufacturer_filter);

    const response = await api.get<DuplicateSupplyPair[]>(
      `/mps/supply-merge/duplicates?${queryParams.toString()}`
    );
    return response.data;
  },

  analyzeMerge: async (primarySupplyId: string, secondarySupplyId: string) => {
    const response = await api.get<SupplyMergeAnalysis>(
      `/mps/supply-merge/analyze?primary_supply_id=${primarySupplyId}&secondary_supply_id=${secondarySupplyId}`
    );
    return response.data;
  },

  executeMerge: async (data: SupplyMergeRequest) => {
    const response = await api.post<SupplyMergeResult>('/mps/supply-merge/merge', data);
    return response.data;
  },

  ignorePair: async (supply1Id: string, supply2Id: string, reason?: string) => {
    const queryParams = new URLSearchParams();
    queryParams.append('supply1_id', supply1Id);
    queryParams.append('supply2_id', supply2Id);
    if (reason) queryParams.append('reason', reason);

    const response = await api.post(`/mps/supply-merge/ignore?${queryParams.toString()}`);
    return response.data;
  },
};

// MFP Model Merge
export interface MFPModelSummary {
  id: string;
  manufacturer: string;
  model_name: string;
  model_code: string;
  equipment_cost: number;
  is_color: boolean;
  is_active: boolean;
  is_sellable: boolean;
  created_at: string;
  equipment_count: number;
  model_supplies_count: number;
  model_accessories_count: number;
  sales_cases_count: number;
  evaluations_count: number;
}

export interface DuplicateModelPair {
  model1: MFPModelSummary;
  model2: MFPModelSummary;
  similarity: number;
  match_type: string;
  recommended_primary: string;
  recommended_action: string;
}

export interface MFPModelMergeAnalysis {
  primary_model: MFPModelSummary;
  secondary_model: MFPModelSummary;
  data_to_migrate: {
    equipment: number;
    model_supplies: number;
    model_accessories: number;
    sales_cases: number;
    evaluations: number;
  };
  conflicts: Array<{
    type: string;
    primary_value: any;
    secondary_value: any;
    resolution: string;
    kept_value: any;
  }>;
  warnings: string[];
  is_safe: boolean;
}

export interface MFPModelMergeRequest {
  primary_model_id: string;
  secondary_model_id: string;
  merge_strategy?: string;
  notes?: string;
  dry_run?: boolean;
}

export interface MFPModelMergeResult {
  success: boolean;
  primary_model_id: string;
  secondary_model_id: string;
  data_migrated: {
    equipment: number;
    model_supplies: number;
    model_accessories: number;
    sales_cases: number;
    evaluations: number;
  };
  conflicts_resolved: Array<{
    type: string;
    action: string;
    result: any;
  }>;
  message: string;
}

export const mfpModelMergeAPI = {
  getDuplicates: async (params?: {
    min_similarity?: number;
    limit?: number;
    manufacturer_filter?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.min_similarity) queryParams.append('min_similarity', String(params.min_similarity));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.manufacturer_filter) queryParams.append('manufacturer_filter', params.manufacturer_filter);

    const response = await api.get<DuplicateModelPair[]>(
      `/mps/model-merge/duplicates?${queryParams.toString()}`
    );
    return response.data;
  },

  analyzeMerge: async (primaryModelId: string, secondaryModelId: string) => {
    const response = await api.get<MFPModelMergeAnalysis>(
      `/mps/model-merge/analyze?primary_model_id=${primaryModelId}&secondary_model_id=${secondaryModelId}`
    );
    return response.data;
  },

  executeMerge: async (data: MFPModelMergeRequest) => {
    const response = await api.post<MFPModelMergeResult>('/mps/model-merge/merge', data);
    return response.data;
  },

  ignorePair: async (model1Id: string, model2Id: string, reason?: string) => {
    const queryParams = new URLSearchParams();
    queryParams.append('model1_id', model1Id);
    queryParams.append('model2_id', model2Id);
    if (reason) queryParams.append('reason', reason);

    const response = await api.post(`/mps/model-merge/ignore?${queryParams.toString()}`);
    return response.data;
  },
};

// Supply KPIs
export interface SupplyKPISummary {
  total_supplies: number;
  total_stock_value: number;
  oem_count: number;
  generic_count: number;
  oem_stock_value: number;
  generic_stock_value: number;
  oem_avg_unit_cost: number;
  generic_avg_unit_cost: number;
  cost_savings_potential: number;
}

export interface CategoryBreakdown {
  category: string;
  count: number;
  total_stock: number;
  total_value: number;
  oem_count: number;
  generic_count: number;
}

export interface SupplyDetail {
  id: string;
  sku: string;
  name: string;
  category: string | null;
  manufacturer: string | null;
  is_original: boolean;
  type: string;
  stock_quantity: number;
  unit_cost: number;
  stock_value: number;
  factory_yield: number | null;
  cost_per_page_factory: number | null;
  real_yield: number | null;
  real_yield_samples: number;
  cost_per_page_real: number | null;
  usage_type: string | null;
}

export interface SupplyKPIData {
  summary: SupplyKPISummary;
  category_breakdown: CategoryBreakdown[];
  supplies: SupplyDetail[];
  note?: string;
}

export const getSupplyKPIs = async (params?: {
  category?: string;
  is_original?: boolean;
  type?: string;
}): Promise<SupplyKPIData> => {
  const queryParams = new URLSearchParams();
  if (params?.category) queryParams.append('category', params.category);
  if (params?.is_original !== undefined) queryParams.append('is_original', String(params.is_original));
  if (params?.type) queryParams.append('type', params.type);

  const response = await api.get<SupplyKPIData>(
    `/mps/supplies/kpis/analytics?${queryParams.toString()}`
  );
  return response.data;
};

// Real Yield Analytics (Per Model)
export interface RealYieldSupply {
  supply_id: string;
  supply_name: string;
  sku: string;
  is_original: boolean;
  type: string;
  advertised_yield: number | null;
  avg_unit_cost: number;
  sample_count: number;
  avg_real_yield: number;
  min_real_yield: number;
  max_real_yield: number;
  stddev_real_yield: number;
  cost_per_1k_pages: number | null;
  yield_efficiency_pct: number | null;
}

export interface ConfidenceIndicator {
  level: 'none' | 'very_low' | 'low' | 'medium' | 'high';
  score: number;
  reason: string;
}

export interface RealYieldTypeStats {
  supply_count: number;
  total_samples: number;
  avg_real_yield: number;
  avg_unit_cost: number;
  cost_per_1k_pages: number;
  confidence: ConfidenceIndicator;
  supplies: RealYieldSupply[];
}

export interface RealYieldModelData {
  model_id: string;
  model_name: string;
  manufacturer: string;
  oem: RealYieldTypeStats;
  generic: RealYieldTypeStats;
  comparison: {
    cheaper_option: string;
    savings_per_1k: number;
    has_both_types: boolean;
  };
}

export interface RealYieldSummary {
  total_models: number;
  models_with_both_types: number;
  models_oem_cheaper: number;
  models_generic_cheaper: number;
  total_oem_samples: number;
  total_generic_samples: number;
}

export interface RealYieldData {
  summary: RealYieldSummary;
  models: RealYieldModelData[];
  methodology: {
    description: string;
    data_source: string;
    minimum_samples: number;
    formula: string;
  };
}

export const getRealYieldAnalytics = async (params?: {
  is_original?: boolean;
  model_id?: string;
}): Promise<RealYieldData> => {
  const queryParams = new URLSearchParams();
  if (params?.is_original !== undefined) {
    queryParams.append('is_original', String(params.is_original));
  }
  if (params?.model_id) {
    queryParams.append('model_id', params.model_id);
  }

  const response = await api.get<RealYieldData>(
    `/mps/supplies/kpis/real-yield?${queryParams.toString()}`
  );
  return response.data;
};

// Early Replacement Alerts
export interface EarlyReplacementAlert {
  dispatch_id: string;
  equipment_id: string;
  supply_id: string;
  serial_number: string;
  model_id: string;
  model_name: string;
  manufacturer: string;
  client_name: string;
  client_id: string;
  supply_name: string;
  sku: string;
  is_original: boolean;
  dispatched_at: string;
  replaced_at: string;
  days_in_service: number | null;
  advertised_yield: number;
  real_yield: number;
  yield_percentage: number;
  unit_cost: number | null;
  waste_value: number;
}

export interface EarlyReplacementAlertsData {
  summary: {
    total_alerts: number;
    total_waste_value: number;
    yield_threshold_pct: number;
    days_lookback: number;
  };
  alerts: EarlyReplacementAlert[];
  methodology?: {
    description: string;
    alert_condition: string;
    waste_calculation: string;
  };
}

export const getEarlyReplacementAlerts = async (params?: {
  yield_threshold_pct?: number;
  days_lookback?: number;
}): Promise<EarlyReplacementAlertsData> => {
  const queryParams = new URLSearchParams();
  if (params?.yield_threshold_pct !== undefined) {
    queryParams.append('yield_threshold_pct', String(params.yield_threshold_pct));
  }
  if (params?.days_lookback !== undefined) {
    queryParams.append('days_lookback', String(params.days_lookback));
  }

  const response = await api.get<EarlyReplacementAlertsData>(
    `/mps/supplies/kpis/early-replacement-alerts?${queryParams.toString()}`
  );
  return response.data;
};

// Defective Supply Statistics
export interface DefectiveSupplySummary {
  total_defective_requests: number;
  total_early_replacements: number;
  defect_rate_pct: number;
  total_defective_cost: number;
  days_lookback: number;
}

export interface DefectiveSupplyBySKU {
  sku: string;
  name: string;
  manufacturer: string;
  is_original: boolean | null;
  defect_count: number;
  total_quantity: number;
  total_cost: number;
}

export interface DefectiveSupplyByManufacturer {
  manufacturer: string;
  defect_count: number;
  total_quantity: number;
  total_cost: number;
}

export interface RecentDefectiveRequest {
  id: string;
  requisition_number: string;
  created_at: string;
  client_name: string;
  sku: string;
  supply_name: string;
  manufacturer: string;
  is_original: boolean | null;
  quantity: number;
  unit_cost: number;
  early_replacement_reason: string | null;
  status: string;
}

export interface DefectiveSupplyStatistics {
  summary: DefectiveSupplySummary;
  by_supply: DefectiveSupplyBySKU[];
  by_manufacturer: DefectiveSupplyByManufacturer[];
  recent_defective: RecentDefectiveRequest[];
}

export const getDefectiveSupplyStatistics = async (params?: {
  days_lookback?: number;
}): Promise<DefectiveSupplyStatistics> => {
  const queryParams = new URLSearchParams();
  if (params?.days_lookback !== undefined) {
    queryParams.append('days_lookback', String(params.days_lookback));
  }

  const response = await api.get<DefectiveSupplyStatistics>(
    `/mps/supplies/kpis/defective-supplies?${queryParams.toString()}`
  );
  return response.data;
};

// Profitability
export interface ProfitabilityPeriod {
  period_start: string;
  period_end: string;
}

export interface RevenueBreakdown {
  lease_revenue: number;
  service_revenue: number;
  copy_revenue_bw: number;
  copy_revenue_color: number;
  total_revenue: number;
}

export interface ProjectedCostBreakdown {
  supply_cost_bw: number;
  supply_cost_color: number;
  equipment_depreciation: number;
  leasing_cost: number;
  service_cost_estimate: number;
  total_cost: number;
}

export interface RealCostBreakdown {
  supply_cost: number;
  equipment_cost: number;
  leasing_cost: number;
  service_cost: number;
  total_cost: number;
}

export interface ContractProfitability {
  contract_id: string;
  contract_number: string;
  contract_type: string;
  client_id: string;
  client_name: string;
  company_name?: string;
  period: ProfitabilityPeriod;
  equipment_count: number;
  revenue: RevenueBreakdown;
  projected_costs: ProjectedCostBreakdown;
  real_costs: RealCostBreakdown;
  projected_profit: number;
  projected_margin_pct: number;
  real_profit: number;
  real_margin_pct: number;
  cost_variance: number;
  profit_variance: number;
  total_bw_copies: number;
  total_color_copies: number;
  service_visits: number;
}

export interface ClientProfitabilitySummary {
  client_id: string;
  client_name: string;
  contracts_count: number;
  equipment_count: number;
  total_revenue: number;
  total_projected_cost: number;
  total_real_cost: number;
  total_projected_profit: number;
  total_real_profit: number;
  avg_projected_margin: number;
  avg_real_margin: number;
  total_cost_variance: number;
  total_profit_variance: number;
}

export interface MonthlyProfitability {
  month: string;
  revenue: number;
  projected_cost: number;
  real_cost: number;
  projected_profit: number;
  real_profit: number;
  bw_copies: number;
  color_copies: number;
  service_visits: number;
}

export interface ProfitabilitySummaryResponse {
  clients: ClientProfitabilitySummary[];
  total_count: number;
  period: ProfitabilityPeriod;
  grand_total_revenue: number;
  grand_total_projected_profit: number;
  grand_total_real_profit: number;
}

export interface ContractProfitabilityListResponse {
  contracts: ContractProfitability[];
  total_count: number;
  period: ProfitabilityPeriod;
}

export interface ContractProfitabilityDetail {
  contract: ContractProfitability;
  monthly_data: MonthlyProfitability[];
}

export const profitabilityAPI = {
  getClientsSummary: async (params?: {
    period_start?: string;
    period_end?: string;
    search?: string;
    min_revenue?: number;
    sort_by?: 'revenue' | 'profit' | 'real_profit' | 'margin';
    sort_order?: 'asc' | 'desc';
    contract_type?: 'leasing' | 'service' | 'combined';
    skip?: number;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.period_start) queryParams.append('period_start', params.period_start);
    if (params?.period_end) queryParams.append('period_end', params.period_end);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.min_revenue) queryParams.append('min_revenue', String(params.min_revenue));
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order);
    if (params?.contract_type) queryParams.append('contract_type', params.contract_type);
    if (params?.skip !== undefined) queryParams.append('skip', String(params.skip));
    if (params?.limit !== undefined) queryParams.append('limit', String(params.limit));

    const response = await api.get<ProfitabilitySummaryResponse>(
      `/profitability/clients?${queryParams.toString()}`
    );
    return response.data;
  },

  getClientDetail: async (clientId: string, params?: {
    period_start?: string;
    period_end?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.period_start) queryParams.append('period_start', params.period_start);
    if (params?.period_end) queryParams.append('period_end', params.period_end);

    const response = await api.get<ClientProfitabilitySummary>(
      `/profitability/clients/${clientId}?${queryParams.toString()}`
    );
    return response.data;
  },

  getContractsProfitability: async (params?: {
    period_start?: string;
    period_end?: string;
    client_id?: string;
    contract_type?: string;
    search?: string;
    skip?: number;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.period_start) queryParams.append('period_start', params.period_start);
    if (params?.period_end) queryParams.append('period_end', params.period_end);
    if (params?.client_id) queryParams.append('client_id', params.client_id);
    if (params?.contract_type) queryParams.append('contract_type', params.contract_type);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.skip !== undefined) queryParams.append('skip', String(params.skip));
    if (params?.limit !== undefined) queryParams.append('limit', String(params.limit));

    const response = await api.get<ContractProfitabilityListResponse>(
      `/profitability/contracts?${queryParams.toString()}`
    );
    return response.data;
  },

  getContractDetail: async (contractId: string, params?: {
    period_start?: string;
    period_end?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.period_start) queryParams.append('period_start', params.period_start);
    if (params?.period_end) queryParams.append('period_end', params.period_end);

    const response = await api.get<ContractProfitabilityDetail>(
      `/profitability/contracts/${contractId}?${queryParams.toString()}`
    );
    return response.data;
  },

  getContractMonthly: async (contractId: string, params?: {
    period_start?: string;
    period_end?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.period_start) queryParams.append('period_start', params.period_start);
    if (params?.period_end) queryParams.append('period_end', params.period_end);

    const response = await api.get<{ contract_id: string; monthly_data: MonthlyProfitability[] }>(
      `/profitability/contracts/${contractId}/monthly?${queryParams.toString()}`
    );
    return response.data;
  },
};

// Contract Update API (for editing leasing costs)
export interface ContractUpdate {
  monthly_lease_fee?: number;
  monthly_service_fee?: number;
  leasing_down_payment?: number;
  leasing_financed_amount?: number;
  leasing_finance_interest?: number;
  estimated_service_visit_cost?: number;
}

export interface ContractResponse {
  id: string;
  contract_number: string;
  contract_type: string;
  client_id: string;
  monthly_lease_fee: number;
  monthly_service_fee: number;
  leasing_down_payment?: number;
  leasing_financed_amount?: number;
  leasing_finance_interest?: number;
  start_date: string;
  end_date?: string;
  term_months?: number;
  status: string;
}

export const contractsAPI = {
  update: async (contractId: string, data: ContractUpdate): Promise<ContractResponse> => {
    const response = await api.put<ContractResponse>(`/mps/contracts/${contractId}`, data);
    return response.data;
  },

  getDetails: async (contractId: string): Promise<ContractResponse> => {
    const response = await api.get<ContractResponse>(`/mps/contracts/${contractId}`);
    return response.data;
  },
};

// Equipment
export interface EquipmentSearchResult {
  serial_number: string;
  id: string;
  model_name?: string;
  manufacturer?: string;
  client_name?: string;
  status: string;
}

export interface EquipmentDetails {
  id: string;
  serial_number: string;
  mfp_model_id: string;
  client_id?: string;
  status: string;
  installation_date?: string;
  ekm_device_id?: string;
  last_meter_bw: number;
  last_meter_color: number;
  notes?: string;
  location?: string;
  zone?: string;
  contact_name?: string;
  contact_phone?: string;
  skip_proration: boolean;
  model_name?: string;
  manufacturer?: string;
  client_name?: string;
  active_contract_id?: string;
  active_contract_number?: string;
  leasing_contract_id?: string;
  leasing_contract_number?: string;
  service_contract_id?: string;
  service_contract_number?: string;
  created_at: string;
  updated_at?: string;
}

export interface CounterReading {
  id: string;
  equipment_id: string;
  reading_date: string;
  bw_counter: number;
  color_counter: number;
  reading_method: string;
  created_at: string;
}

export const equipmentAPI = {
  searchSerial: async (query: string): Promise<EquipmentSearchResult[]> => {
    const response = await api.get(`/mps/equipment/search/serial-autocomplete?q=${encodeURIComponent(query)}`);
    return response.data;
  },

  getBySerial: async (serial: string): Promise<EquipmentDetails> => {
    const response = await api.get(`/mps/equipment/by-serial/${encodeURIComponent(serial)}`);
    return response.data;
  },

  update: async (id: string, data: Record<string, unknown>): Promise<EquipmentDetails> => {
    const response = await api.patch(`/mps/equipment/${id}`, data);
    return response.data;
  },

  getCounterReadings: async (equipmentId: string, skip = 0, limit = 100): Promise<CounterReading[]> => {
    const response = await api.get(`/mps/counter-readings/equipment/${equipmentId}/history?skip=${skip}&limit=${limit}`);
    return response.data;
  },
};

// Inventory Adjustment Audit Log
export interface InventoryAdjustmentLogItem {
  id: string;
  supply_sku: string;
  supply_name: string;
  previous_quantity: number;
  new_quantity: number;
  adjustment_quantity: number;
  reason: string;
  adjustment_type: string;
  adjusted_by_user_name: string | null;
  created_at: string;
}

export interface InventoryAdjustmentHistoryResponse {
  total: number;
  items: InventoryAdjustmentLogItem[];
}

export const inventoryAdjustmentAPI = {
  getHistory: async (skip?: number, limit?: number, supplySku?: string): Promise<InventoryAdjustmentHistoryResponse> => {
    const params = new URLSearchParams();
    if (skip !== undefined) params.append('skip', skip.toString());
    if (limit !== undefined) params.append('limit', limit.toString());
    if (supplySku) params.append('supply_sku', supplySku);
    const response = await api.get<InventoryAdjustmentHistoryResponse>(
      `/mps/supplies/inventory-adjustment-history?${params.toString()}`
    );
    return response.data;
  },
};

// Fleet Management
export const fleetAdminAPI = {
  getFleetClients: async (): Promise<any[]> => {
    const response = await api.get('/fleet/admin/clients');
    return response.data;
  },

  getFleetAgents: async (clientId?: string): Promise<any[]> => {
    const url = clientId
      ? `/fleet/admin/agents?client_id=${clientId}`
      : '/fleet/admin/agents';
    const response = await api.get(url);
    return response.data;
  },

  generateAgentKey: async (clientId: string): Promise<{ registration_key: string }> => {
    const response = await api.post(`/fleet/agents/generate-key?client_id=${clientId}`);
    return response.data;
  },

  triggerADSync: async (clientId: string, config: {
    tenant_id: string;
    client_id_azure: string;
    client_secret: string;
  }): Promise<any> => {
    const response = await api.post(`/fleet/admin/ad-config/${clientId}/sync`, config);
    return response.data;
  },

  getFleetAnalytics: async (): Promise<any> => {
    const response = await api.get('/fleet/admin/analytics');
    return response.data;
  },

  getAlertRules: async (clientId: string): Promise<any[]> => {
    const response = await api.get(`/fleet/admin/alerts/${clientId}`);
    return response.data;
  },

  setAlertRules: async (clientId: string, rules: any[]): Promise<any> => {
    const response = await api.post(`/fleet/admin/alerts/${clientId}`, rules);
    return response.data;
  },
};

// Sales Evaluations (Admin - all users)
export const salesEvaluationsAPI = {
  getAll: async (): Promise<SalesEvaluation[]> => {
    const response = await api.get('/sales/evaluations/admin/all?limit=500');
    return response.data;
  },

  updateStatus: async (evaluationId: string, status: EvaluationStatus): Promise<SalesEvaluation> => {
    const response = await api.patch(`/sales/evaluations/${evaluationId}`, { status });
    return response.data;
  },

  delete: async (evaluationId: string): Promise<void> => {
    await api.delete(`/sales/evaluations/${evaluationId}`);
  },

  generateProposal: async (evaluationId: string): Promise<Blob> => {
    const response = await api.get(`/sales/evaluations/${evaluationId}/proposal`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Effective Coverage
export interface CoverageSupply {
  colour: string;
  description: string;
  sku: string;
  target_yield: number;
  engine_cycles: number;
  percent_left: number;
  days_monitored: number;
  achieved_yield: number;
  performance: number;
  effective_coverage: number;
}

export interface CoverageDevice {
  device_id: number;
  serial_number: string;
  client_name: string;
  client_id: string;
  model: string;
  portal: string;
  avg_effective_coverage: number;
  supplies: CoverageSupply[];
}

export interface CoverageSummary {
  total_devices: number;
  total_supplies_analyzed: number;
  avg_effective_coverage: number;
  high_coverage_count: number;
  high_coverage_threshold: number;
}

export interface EffectiveCoverageData {
  devices: CoverageDevice[];
  summary: CoverageSummary;
}

export const getEffectiveCoverage = async (clientId?: string, limit?: number): Promise<EffectiveCoverageData> => {
  const params = new URLSearchParams();
  if (clientId) params.append('client_id', clientId);
  params.append('limit', String(limit || 2500));
  const response = await api.get<EffectiveCoverageData>(
    `/mps/ekm-sync/effective-coverage?${params.toString()}`
  );
  return response.data;
};

export default api;
